package org.example.project.service

import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.LumaticaApiSettings
import org.example.project.utils.All
import org.w3c.fetch.RequestInit

@Serializable
private data class YardEngineRequest(
    val auftrag: All? = null,
    val booking: All? = null,
    val artCode: String = "",
    val firmenId: String = "",
    val tor: All? = null,
    val standortDetail: All? = null,
    val wert: String = ""
)

@Serializable
data class YardEngineTorSuggestion(
    val auftrag: All = All(),
    val torSuggested: Boolean = false
)

object YardEngineApi {
    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = false
    }

    suspend fun fahrerAngemeldet(auftrag: All, artCode: String, firmenId: String = ""): All? =
        callAll("zustand", "fahrer-angemeldet", YardEngineRequest(auftrag = auftrag, artCode = artCode, firmenId = firmenId))

    suspend fun maAnsTor(
        auftrag: All,
        artCode: String,
        firmenId: String = "",
        tor: All? = null,
        standortDetail: All? = null
    ): All? =
        callAll(
            "torvergabe",
            "ma-ans-tor",
            YardEngineRequest(
                auftrag = auftrag,
                artCode = artCode,
                firmenId = firmenId,
                tor = tor,
                standortDetail = standortDetail
            )
        )

    suspend fun fahrerAngesetzt(
        auftrag: All,
        artCode: String,
        firmenId: String = ""
    ): All? =
        callAll("zustand", "fahrer-angesetzt", YardEngineRequest(auftrag = auftrag, artCode = artCode, firmenId = firmenId))

    suspend fun pinBestaetigt(auftrag: All, artCode: String, firmenId: String = ""): All? =
        callAll("zustand", "pin-bestaetigt", YardEngineRequest(auftrag = auftrag, artCode = artCode, firmenId = firmenId))

    suspend fun weAbschluss(
        auftrag: All,
        firmenId: String = "",
        istPaletten: String = ""
    ): All? =
        callAll("yardabschluss", "we-abschluss", YardEngineRequest(auftrag = auftrag, firmenId = firmenId, wert = istPaletten))

    suspend fun weTeilabschluss(
        auftrag: All,
        firmenId: String = "",
        istPaletten: String = ""
    ): All? =
        callAll("yardabschluss", "we-teilabschluss", YardEngineRequest(auftrag = auftrag, firmenId = firmenId, wert = istPaletten))

    suspend fun waKontrolle(
        kopf: All,
        standort: All,
        firmenId: String = ""
    ): Boolean {
        val ok = callRaw(
            "kontrolle",
            "wa-kontrolle",
            YardEngineRequest(auftrag = kopf, standortDetail = standort, firmenId = firmenId)
        ).contains("\"ok\":true")
        if (ok) {
            YardLiveSync.markWaStandortChanged(kopf, standort, firmenId)
        }
        return ok
    }

    suspend fun waLkwAbschluss(
        auftrag: All,
        firmenId: String = "",
        leergut: String = "",
        istPaletten: String = ""
    ): All? =
        callAll(
            "yardabschluss",
            "wa-lkw-abschluss",
            YardEngineRequest(
                auftrag = auftrag,
                firmenId = firmenId,
                wert = leergut
            )
        )

    suspend fun waDokumentabschluss(
        auftrag: All,
        firmenId: String = ""
    ): All? =
        callAll(
            "yardabschluss",
            "wa-dokumentabschluss",
            YardEngineRequest(
                auftrag = auftrag,
                firmenId = firmenId
            )
        )

    suspend fun suggestWeTor(auftrag: All, firmenId: String = ""): YardEngineTorSuggestion? =
        call("torvergabe", "suggest-we-tor", YardEngineRequest(auftrag = auftrag, firmenId = firmenId)) { raw ->
            json.decodeFromString(YardEngineTorSuggestion.serializer(), raw)
        }

    suspend fun syncSlotBooking(booking: All, firmenId: String = "", slotAction: String = "sync"): Boolean =
        callSlotRaw(slotAction, YardEngineRequest(booking = booking, firmenId = firmenId)).contains("\"ok\":true")

    suspend fun cancelSlotBooking(booking: All, firmenId: String = "", slotAction: String = "cancel"): Boolean =
        callSlotRaw(slotAction, YardEngineRequest(booking = booking, firmenId = firmenId)).contains("\"ok\":true")

    private suspend fun callAll(api: String, action: String, body: YardEngineRequest): All? {
        val saved = call(api, action, body) { raw ->
            json.decodeFromString(All.AllNames.serializer(), raw)
        }
        triggerYardRefresh(saved)
        return saved
    }

    private suspend fun <T> call(api: String, action: String, body: YardEngineRequest, decode: (String) -> T): T? =
        decode(callRaw(api, action, body))

    private suspend fun callRaw(api: String, action: String, body: YardEngineRequest): String {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/$api/$action",
            RequestInit(
                method = "POST",
                headers = LumaticaApiManager.auditHeaders("application/json"),
                body = json.encodeToString(YardEngineRequest.serializer(), body)
            )
        ).await()
        if (!response.ok) error("YardEngineApi $api/$action failed: ${response.status}")
        return response.text().await()
    }

    private fun triggerYardRefresh(saved: All?) {
        YardLiveSync.markAuftragChanged(saved)
    }

    private suspend fun callSlotRaw(action: String, body: YardEngineRequest): String {
        val apiPath = when (action) {
            "we-single" -> "slot_we_single/sync"
            "we-multi" -> "slot_we_multi/sync"
            "we-single-cancel" -> "slot_we_single/cancel"
            "we-multi-cancel" -> "slot_we_multi/cancel"
            "wa-single" -> "slot_wa_single/sync"
            "wa-multi" -> "slot_wa_multi/sync"
            "wa-single-cancel" -> "slot_wa_single/cancel"
            "wa-multi-cancel" -> "slot_wa_multi/cancel"
            else -> error("Unbekannte Slot-Aktion: $action")
        }
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/$apiPath",
            RequestInit(
                method = "POST",
                headers = LumaticaApiManager.auditHeaders("application/json"),
                body = json.encodeToString(YardEngineRequest.serializer(), body)
            )
        ).await()
        if (!response.ok) error("YardEngineApi slot $apiPath failed: ${response.status}")
        return response.text().await()
    }
}
